// Core React Imports
import { useState, useEffect, useRef, useMemo } from "react";

// Local Imports
import ApiService from "../../data/ApiService";
import UtilsUnit from "../../utils/UtilsUnit";
import useUtilsUnit from "../../utils/useUtilsUnit";
import { useWidgetFilterDropdowns } from "../../widgets/dropdowns/widget-filter-dropdowns/useWidgetFilterDropdowns";
import ConstantsTexts from "../../constants/ConstantsTexts";
import { useLoading } from "../../widgets/loadings/WidgetLoading";
import ExcelExportUtils from "../../utils/ExcelExportUtils";
import { useMapMarkersPolygons } from "../../widgets/maps/useMapMarkersPolygons";
import { seedTerminalPolygonCacheFromFilterData, mergeFilterPolygonsOntoMarkers } from "../../utils/TerminalPolygonCache";

const isModelVersionFrozen = (version) => version?.isFrozen === true;

// Prefer terminalID — erezSKU is missing from frozen dashboard payloads (and can be null/duplicate).
const getTerminalRowKey = (terminal) => {
  if (terminal?.terminalID != null && terminal.terminalID !== "") {
    return terminal.terminalID;
  }

  if (terminal?.erezSKU != null && terminal.erezSKU !== "") {
    return terminal.erezSKU;
  }

  return null;
};

const isTerminalRowSelected = (terminal, selectedItemId) => {
  if (selectedItemId == null || selectedItemId === "") {
    return false;
  }

  const rowKey = getTerminalRowKey(terminal);
  if (rowKey == null || rowKey === "") {
    return false;
  }

  return String(rowKey) === String(selectedItemId);
};

const resolveModelVersionOption = (modelVersionInt, versions) => {
  if (modelVersionInt == null || !versions?.length) {
    return null;
  }

  return versions.find((version) => Number(version.modelVersionInt) === Number(modelVersionInt)) ?? null;
};

export function usePageDashboard() {
  const { userData } = useUtilsUnit({ userData: true });
  const [filterData, setFilterData] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const [data, setData] = useState([]);
  const [dataIsReceived, setDataIsReceived] = useState(false);
  const [showSearchTerminal, setShowSearchTerminal] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [selectedTerminalFromList, setSelectedTerminalFromList] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const tooltipRef = useRef(null);
  const listRef = useRef(null);
  const [selectedModelVersion, setSelectedModelVersion] = useState(null);
  const [selectedModelVersion2, setSelectedModelVersion2] = useState(null);

  // Data from API
  const [nationalModelForTerminal, setNationalModelForTerminal] = useState(null);
  const [nationalModelForTerminalFiltered, setNationalModelForTerminalFiltered] = useState(null);
  const [mainMeasure, setMainMeasure] = useState(null);
  const [secondaryMeasure, setSecondaryMeasure] = useState(null);
  const [secondaryMeasureDetails, setSecondaryMeasureDetails] = useState(null);
  const [terminalNameSlicer, setTerminalNameSlicer] = useState([]);
  const [municipalitySlicer, setMunicipalitySlicer] = useState([]);
  const [districtSlicer, setDistrictSlicer] = useState([]);
  const [terminalTypeSlicer, setTerminalTypeSlicer] = useState([]);
  const [terminalStatusSlicer, setTerminalStatusSlicer] = useState([]);
  const [budgetStatusSlicer, setBudgetStatusSlicer] = useState([]);
  const [organizationInChargeSlicer, setOrganizationInChargeSlicer] = useState([]);
  const [modelVersions, setModelVersions] = useState([]);

  // Comparison state
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [mainMeasure2, setMainMeasure2] = useState(null);
  const [secondaryMeasure2, setSecondaryMeasure2] = useState(null);

  // Dropdown
  const [selectedTerminalNameSlicer, setSelectedTerminalNameSlicer] = useState(null);
  const [selectedMunicipalitySlicer, setSelectedMunicipalitySlicer] = useState([]);
  const [selectedDistrictSlicer, setSelectedDistrictSlicer] = useState([]);
  const [selectedTerminalTypeSlicer, setSelectedTerminalTypeSlicer] = useState([]);
  const [selectedTerminalStatusSlicer, setSelectedTerminalStatusSlicer] = useState([]);
  const [selectedBudgetStatusSlicer, setSelectedBudgetStatusSlicer] = useState([]);
  const [selectedOrganizationInChargeSlicer, setSelectedOrganizationInChargeSlicer] = useState([]);

  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);

  // useWidgetFilterDropdowns - start
  const { uniqueDistricts, uniqueMunicipalities, uniqueTerminals } = useWidgetFilterDropdowns(filterData, selectedDistrict, selectedMunicipality);
  // useWidgetFilterDropdowns - end

  // Fetch data on component mount
  useEffect(() => {
    (async () => {
      await fetchFilterData();
      await fetchInitialDashboardData();
      setDataIsReceived(true);
    })();
  }, []);

  // Updates active filters state by checking if any slicer has selected values
  useEffect(() => {
    setHasActiveFilters(
      !!(
        selectedTerminalNameSlicer ||
        (selectedMunicipalitySlicer && selectedMunicipalitySlicer.length > 0) ||
        (selectedDistrictSlicer && selectedDistrictSlicer.length > 0) ||
        (selectedTerminalTypeSlicer && selectedTerminalTypeSlicer.length > 0) ||
        (selectedTerminalStatusSlicer && selectedTerminalStatusSlicer.length > 0) ||
        (selectedBudgetStatusSlicer && selectedBudgetStatusSlicer.length > 0) ||
        (selectedOrganizationInChargeSlicer && selectedOrganizationInChargeSlicer.length > 0)
      )
    );
  }, [
    selectedTerminalNameSlicer,
    selectedMunicipalitySlicer,
    selectedDistrictSlicer,
    selectedTerminalTypeSlicer,
    selectedTerminalStatusSlicer,
    selectedBudgetStatusSlicer,
    selectedOrganizationInChargeSlicer,
  ]);

  // Closes tooltip when clicking outside, with exception for tooltip icons
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on a tooltip icon
      if (event.target.closest(".dashboard-3-frame-4")) {
        return;
      }
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setActiveTooltip(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Set initial selected model version when modelVersions data is loaded
  useEffect(() => {
    if (modelVersions && modelVersions.length > 0 && !selectedModelVersion) {
      // Select the first model version by default
      setSelectedModelVersion(modelVersions[0]);
    }
  }, [modelVersions, selectedModelVersion]);

  // Set initial selected model version 2 when modelVersions data is loaded
  useEffect(() => {
    if (modelVersions && modelVersions.length > 1 && !selectedModelVersion2) {
      // Select the second model version by default for comparison, but only if it's different from the first
      const secondVersion = modelVersions[1];
      if (secondVersion && (!selectedModelVersion || secondVersion.modelVersionInt !== selectedModelVersion.modelVersionInt)) {
        setSelectedModelVersion2(secondVersion);
      }
    }
  }, [modelVersions, selectedModelVersion2, selectedModelVersion]);

  // Filters terminal list based on selected slicer values and updates single-select slicer options
  useEffect(() => {
    if (!nationalModelForTerminal) return;

    let filteredList = [...nationalModelForTerminal];

    // Apply filters for each selected slicer
    if (selectedMunicipalitySlicer && selectedMunicipalitySlicer.length > 0) {
      const municipalityNames = selectedMunicipalitySlicer.map((item) => item.municipalityName);
      filteredList = filteredList.filter((item) => municipalityNames.includes(item.municipalityName));
    }
    if (selectedDistrictSlicer && selectedDistrictSlicer.length > 0) {
      const districtNames = selectedDistrictSlicer.map((item) => item.districtMOTName);
      filteredList = filteredList.filter((item) => districtNames.includes(item.districtMOT));
    }
    if (selectedTerminalTypeSlicer && selectedTerminalTypeSlicer.length > 0) {
      const terminalTypeNames = selectedTerminalTypeSlicer.map((item) => item.terminalTypeName);
      filteredList = filteredList.filter((item) => terminalTypeNames.includes(item.terminalType));
    }
    if (selectedTerminalStatusSlicer && selectedTerminalStatusSlicer.length > 0) {
      const terminalStatusNames = selectedTerminalStatusSlicer.map((item) => item.terminalStatusName);
      filteredList = filteredList.filter((item) => terminalStatusNames.includes(item.terminalStatus));
    }
    if (selectedBudgetStatusSlicer && selectedBudgetStatusSlicer.length > 0) {
      const budgetStatusNames = selectedBudgetStatusSlicer.map((item) => item.budgetStatusName);
      filteredList = filteredList.filter((item) => budgetStatusNames.includes(item.budgetStatus));
    }
    if (selectedOrganizationInChargeSlicer && selectedOrganizationInChargeSlicer.length > 0) {
      const organizationNames = selectedOrganizationInChargeSlicer.map((item) => item.organizationInChargeName);
      filteredList = filteredList.filter((item) => organizationNames.includes(item.organizationInCharge));
    }

    // Check if selected מתקן (terminal name + erez SKU) exists in filtered list
    if (selectedTerminalNameSlicer?.terminal_info) {
      const [terminalName, erezSku] = selectedTerminalNameSlicer.terminal_info.split(" | ");
      const terminalExists = filteredList.some((item) => item.terminalName?.trim() === terminalName && String(item.erezSKU)?.trim() === (erezSku || "").trim());

      if (!terminalExists) {
        setSelectedTerminalNameSlicer(null);
        resetSelectedItemIdAndTerminalFromList();
      }
    }

    setNationalModelForTerminalFiltered(filteredList);

    // Update only מתקן (terminal name + erez SKU) slicer options based on filtered list
    // The other slicers (multi-select filters) will only get their data from fetchData
    const uniqueTerminalNamesSlicer = [...new Set(filteredList.map((item) => `${item.terminalName} | ${item.erezSKU}`))].map((name) => ({
      terminal_info: name,
    }));

    setTerminalNameSlicer(uniqueTerminalNamesSlicer);
  }, [
    nationalModelForTerminal,
    selectedTerminalNameSlicer,
    selectedMunicipalitySlicer,
    selectedDistrictSlicer,
    selectedTerminalTypeSlicer,
    selectedTerminalStatusSlicer,
    selectedBudgetStatusSlicer,
    selectedOrganizationInChargeSlicer,
  ]);

  // Fetch filter data function
  const fetchFilterData = async () => {
    showLoadingRef.current();

    try {
      const filterDataResponse = await ApiService.getDataForFiltering();
      // Same filter payload includes terminalGeoJSON — seed cache for map enrichment
      seedTerminalPolygonCacheFromFilterData(filterDataResponse, 0);
      setFilterData(filterDataResponse);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Fetches dashboard data from API and processes the response
  const fetchData = async (modelVersionInt) => {
    showLoadingRef.current();

    try {
      const fetchedData = await ApiService.getDashboardData(modelVersionInt);
      handleFechedAPIData(fetchedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  const fetchInitialDashboardData = async () => {
    showLoadingRef.current();

    try {
      const fetchedData = await ApiService.getDashboardData(0);
      handleFechedAPIData(fetchedData);

      if (fetchedData?.modelVersions?.length > 0) {
        setSelectedModelVersion(fetchedData.modelVersions[0]);
      }
    } catch (error) {
      console.error("Error fetching initial dashboard data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Fetches comparison data for two model versions and sets both primary and secondary measures
  const fetchComparisonData = async (modelVersion1, modelVersion2) => {
    showLoadingRef.current();

    try {
      const comparisonData = await ApiService.getDashboardDataComparison(modelVersion1, modelVersion2);

      // Set comparison data
      setMainMeasure(comparisonData?.mainMeasure);
      setSecondaryMeasure(comparisonData?.secondaryMeasure);
      setMainMeasure2(comparisonData?.mainMeasure2);
      setSecondaryMeasure2(comparisonData?.secondaryMeasure2);
      setNationalModelForTerminal(comparisonData.nationalModelForTerminal);
      setNationalModelForTerminalFiltered(comparisonData.nationalModelForTerminal);
      const versions = comparisonData.modelVersions ?? [];
      setModelVersions(versions);
      setSelectedModelVersion((prev) => resolveModelVersionOption(modelVersion1, versions) ?? prev);
      setSelectedModelVersion2((prev) => resolveModelVersionOption(modelVersion2, versions) ?? prev);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  const resetComparisonData = () => {
    setMainMeasure2(null);
    setSecondaryMeasure2(null);
    setSelectedModelVersion2(null);
  };

  const resetFilters = () => {
    setSelectedTerminalNameSlicer(null);
    setSelectedMunicipalitySlicer([]);
    setSelectedDistrictSlicer([]);
    setSelectedTerminalTypeSlicer([]);
    setSelectedTerminalStatusSlicer([]);
    setSelectedBudgetStatusSlicer([]);
    setSelectedOrganizationInChargeSlicer([]);
  };

  function resetSelectedItemIdAndTerminalFromList() {
    setSelectedItemId(null);
    setSelectedTerminalFromList(null);
  }

  function handleFechedAPIData(fetchedData) {
    setData(fetchedData);
    setDataFromFetchedData(fetchedData);
  }

  function setDataFromFetchedData(fetchedData) {
    setNationalModelForTerminal(fetchedData?.nationalModelForTerminal);
    setNationalModelForTerminalFiltered(fetchedData?.nationalModelForTerminal);
    setMainMeasure(fetchedData?.mainMeasure);
    setSecondaryMeasure(fetchedData?.secondaryMeasure);
    setSecondaryMeasureDetails(fetchedData?.secondaryMeasureDetails);
    setModelVersions(fetchedData?.modelVersions);

    // Initialize slicer options from the initial data (only from fetchData)
    if (fetchedData?.nationalModelForTerminal) {
      const initialData = fetchedData.nationalModelForTerminal;

      // Initialize multi-select slicers with all available options from the data
      const uniqueMunicipalitiesSlicer = [...new Set(initialData.map((item) => item.municipalityName))].map((name) => ({ municipalityName: name }));
      const uniqueDistrictsSlicer = [...new Set(initialData.map((item) => item.districtMOT).filter(Boolean))].map((name) => ({ districtMOTName: name }));
      const uniqueTerminalTypesSlicer = [...new Set(initialData.map((item) => item.terminalType))].map((name) => ({ terminalTypeName: name }));
      const uniqueTerminalStatusesSlicer = [...new Set(initialData.map((item) => item.terminalStatus))].map((name) => ({ terminalStatusName: name }));
      const uniqueBudgetStatusesSlicer = [...new Set(initialData.map((item) => item.budgetStatus))].map((name) => ({ budgetStatusName: name }));
      const uniqueOrganizationsSlicer = [...new Set(initialData.map((item) => item.organizationInCharge))].map((name) => ({ organizationInChargeName: name }));

      setMunicipalitySlicer(uniqueMunicipalitiesSlicer);
      setDistrictSlicer(uniqueDistrictsSlicer);
      setTerminalTypeSlicer(uniqueTerminalTypesSlicer);
      setTerminalStatusSlicer(uniqueTerminalStatusesSlicer);
      setBudgetStatusSlicer(uniqueBudgetStatusesSlicer);
      setOrganizationInChargeSlicer(uniqueOrganizationsSlicer);

      // Also initialize the single-select slicer for מתקן (terminal name + erez SKU)
      const uniqueTerminalNamesSlicer = [...new Set(initialData.map((item) => `${item.terminalName} | ${item.erezSKU}`))].map((name) => ({
        terminal_info: name,
      }));

      setTerminalNameSlicer(uniqueTerminalNamesSlicer);
    }
  }

  // handle - logic
  // useWidgetFilterDropdowns - start
  const handleSelectDropdownDistrict = (option) => {
    setSelectedDistrict(option);
    setSelectedMunicipality(null);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownMunicipality = (option) => {
    setSelectedMunicipality(option);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownTerminal = (option) => {
    setSelectedTerminal(option);
  };
  // useWidgetFilterDropdowns - end

  const handleSearchTerminalIconClick = () => {
    setShowSearchTerminal(!showSearchTerminal);
  };

  const handleSelectTerminalFromList = (terminal) => {
    const rowKey = getTerminalRowKey(terminal);
    setSelectedTerminalFromList(terminal);
    setSelectedItemId(rowKey);
    setSelectedTerminalNameSlicer({ terminal_info: `${terminal.terminalName} | ${terminal.erezSKU}` });
  };

  const handleTooltipClick = (tooltipId) => {
    setActiveTooltip((prevTooltip) => (prevTooltip === tooltipId ? null : tooltipId));
  };

  function getMainMeasurePercentBase(serial, typeVersion) {
    return UtilsUnit.getValueFromList(serial, typeVersion, "mainMeasureSerial", "mainMeasurePercent");
  }

  function getMainMeasurePercent(serial) {
    return getMainMeasurePercentBase(serial, mainMeasure);
  }

  function getMainMeasurePercentComparison(serial) {
    return getMainMeasurePercentBase(serial, mainMeasure2);
  }

  function getSecondaryMeasurePercentForDisplayBase(serial, typeVersion) {
    return UtilsUnit.getValueFromList(serial, typeVersion, "secondaryMeasureSerial", "secondaryMeasurePercentForDisplay");
  }

  function getSecondaryMeasurePercentForDisplay(serial) {
    return getSecondaryMeasurePercentForDisplayBase(serial, secondaryMeasure);
  }

  function getSecondaryMeasurePercentForDisplayComparison(serial) {
    return getSecondaryMeasurePercentForDisplayBase(serial, secondaryMeasure2);
  }

  function getSecondaryMeasureDetails(serial) {
    return UtilsUnit.getValueFromList(serial, secondaryMeasureDetails, "secondaryMeasureSerial", "details");
  }

  function getZoomCoordinates() {
    if (selectedTerminalFromList) {
      return {
        lat: selectedTerminalFromList.latitude,
        lng: selectedTerminalFromList.longitude,
      };
    }
    return null;
  }

  // Scrolls to selected terminal in the list and updates selection state
  const scrollToSelectedItem = (selectedValue) => {
    if (!listRef.current || !nationalModelForTerminalFiltered) return;

    // Find the index of the selected item in the filtered list (מתקן = name | erezSKU)
    const selectedIndex = nationalModelForTerminalFiltered.findIndex((item) => {
      const terminalNameWithErezSku = `${item.terminalName} | ${item.erezSKU}`;
      return selectedValue === terminalNameWithErezSku;
    });

    if (selectedIndex === -1) return;

    // Scroll to the selected index
    listRef.current.scrollToItem(selectedIndex, "center");

    // Find the matching terminal object in the filtered model
    const selectedTerminal = nationalModelForTerminalFiltered[selectedIndex];

    if (selectedTerminal) {
      setSelectedItemId(getTerminalRowKey(selectedTerminal));
      setSelectedTerminalFromList(selectedTerminal);
    }
  };

  const handleSelectTerminalNameSlicer = (option) => {
    handleSelectOption(option, "terminal_info", setSelectedTerminalNameSlicer);

    if (option) {
      scrollToSelectedItem(option.terminal_info);
    }
  };

  const handleSelectMunicipalitySlicer = (options) => {
    // For multi-select, options is an array
    if (Array.isArray(options)) {
      setSelectedMunicipalitySlicer(options);
    } else {
      // Fallback for single selection
      handleSelectOption(options, "municipalityName", setSelectedMunicipalitySlicer);
    }
  };

  const handleSelectDistrictSlicer = (options) => {
    // For multi-select, options is an array
    if (Array.isArray(options)) {
      setSelectedDistrictSlicer(options);
    } else {
      // Fallback for single selection
      handleSelectOption(options, "districtMOTName", setSelectedDistrictSlicer);
    }
  };

  const handleSelectTerminalTypeSlicer = (options) => {
    // For multi-select, options is an array
    if (Array.isArray(options)) {
      setSelectedTerminalTypeSlicer(options);
    } else {
      // Fallback for single selection
      handleSelectOption(options, "terminalTypeName", setSelectedTerminalTypeSlicer);
    }
  };

  const handleSelectTerminalStatusSlicer = (options) => {
    // For multi-select, options is an array
    if (Array.isArray(options)) {
      setSelectedTerminalStatusSlicer(options);
    } else {
      // Fallback for single selection
      handleSelectOption(options, "terminalStatusName", setSelectedTerminalStatusSlicer);
    }
  };

  const handleSelectBudgetStatusSlicer = (options) => {
    // For multi-select, options is an array
    if (Array.isArray(options)) {
      setSelectedBudgetStatusSlicer(options);
    } else {
      // Fallback for single selection
      handleSelectOption(options, "budgetStatusName", setSelectedBudgetStatusSlicer);
    }
  };

  const handleSelectOrganizationInChargeSlicer = (options) => {
    // For multi-select, options is an array
    if (Array.isArray(options)) {
      setSelectedOrganizationInChargeSlicer(options);
    } else {
      // Fallback for single selection
      handleSelectOption(options, "organizationInChargeName", setSelectedOrganizationInChargeSlicer);
    }
  };

  const isSameModelVersion = (a, b) => a != null && b != null && Number(a.modelVersionInt) === Number(b.modelVersionInt);

  const getFilteredModelVersionsForFirst = () => {
    if (!selectedModelVersion2 || !modelVersions) {
      return modelVersions || [];
    }

    return modelVersions.filter((version) => !isSameModelVersion(version, selectedModelVersion2));
  };

  const getFilteredModelVersionsForSecond = () => {
    if (!selectedModelVersion || !modelVersions) {
      return modelVersions || [];
    }

    return modelVersions.filter((version) => !isSameModelVersion(version, selectedModelVersion));
  };

  const handleSelectModelVersion = async (option) => {
    if (option === null) {
      setSelectedModelVersion(null);
      return;
    }

    if (isComparisonMode && isSameModelVersion(option, selectedModelVersion2)) {
      return;
    }

    const resolved = resolveModelVersionOption(option.modelVersionInt, modelVersions) ?? option;
    setSelectedModelVersion(resolved);

    // If in comparison mode and both versions are selected, fetch comparison data
    if (isComparisonMode && selectedModelVersion2) {
      await fetchComparisonData(resolved.modelVersionInt, selectedModelVersion2.modelVersionInt);
    } else {
      fetchData(resolved.modelVersionInt);
    }
  };

  const handleSelectModelVersion2 = async (option) => {
    if (option === null) {
      setSelectedModelVersion2(null);
      return;
    }

    if (isComparisonMode && isSameModelVersion(option, selectedModelVersion)) {
      return;
    }

    const resolved = resolveModelVersionOption(option.modelVersionInt, modelVersions) ?? option;
    setSelectedModelVersion2(resolved);

    // If in comparison mode and both versions are selected, fetch comparison data
    if (isComparisonMode && selectedModelVersion) {
      await fetchComparisonData(selectedModelVersion.modelVersionInt, resolved.modelVersionInt);
    }
  };

  const handleSelectOption = (option, key, setInputValue) => {
    if (option === null) {
      setInputValue(null);
      return;
    }

    const selectedObj = {
      [key]: option[key],
    };

    setInputValue(selectedObj);
  };

  // Prepares request body for Excel export with current filter selections and model versions
  const prepareExcelRequestBody = () => {
    return {
      ...(isComparisonMode ? { modelVersion1: selectedModelVersion?.modelVersionInt } : { modelVersion: selectedModelVersion?.modelVersionInt }),
      modelVersion2: isComparisonMode ? selectedModelVersion2?.modelVersionInt : null,
      municipalityName: selectedMunicipalitySlicer?.length > 0 ? selectedMunicipalitySlicer.map((item) => item.municipalityName) : null,
      districtMOT: selectedDistrictSlicer?.length > 0 ? selectedDistrictSlicer.map((item) => item.districtMOTName) : null,
      terminalType: selectedTerminalTypeSlicer?.length > 0 ? selectedTerminalTypeSlicer.map((item) => item.terminalTypeName) : null,
      terminalStatus: selectedTerminalStatusSlicer?.length > 0 ? selectedTerminalStatusSlicer.map((item) => item.terminalStatusName) : null,
      budgetStatus: selectedBudgetStatusSlicer?.length > 0 ? selectedBudgetStatusSlicer.map((item) => item.budgetStatusName) : null,
      organizationInCharge: selectedOrganizationInChargeSlicer?.length > 0 ? selectedOrganizationInChargeSlicer.map((item) => item.organizationInChargeName) : null,
    };
  };

  // Orchestrates the complete Excel export process with data fetching, formatting, and download
  const exportToExcel = async () => {
    // Check if filtered rows length is 0 and show alert instead of calling API
    if (!nationalModelForTerminalFiltered || nationalModelForTerminalFiltered.length === 0) {
      alert(ConstantsTexts.CODE_234);
      return;
    }

    showLoadingRef.current();

    try {
      const fileName = ConstantsTexts.CODE_155;

      // Prepare the request body with slicer values
      const requestBody = prepareExcelRequestBody();

      // Get the Excel data from the API with the slicer values
      const excelData = isComparisonMode ? await ApiService.exportDashboardExcelDataComparison(requestBody) : await ApiService.exportDashboardExcelData(requestBody);

      if (!excelData || !excelData.length) return;

      // Use the Excel export utility to handle the export process
      await ExcelExportUtils.exportToExcel(excelData, isComparisonMode, selectedModelVersion, selectedModelVersion2, fileName);
    } catch (err) {
      console.error("Error exporting Excel:", err);
    }

    hideLoadingRef.current();
  };

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  // Clears filter selections with different behavior for multi-select vs single-select filters
  const handleClear = (e, setInputValue, isTerminalSlicer = false) => {
    e.stopPropagation();

    // For multi-select filters, clear to empty array, otherwise clear to null
    if (
      setInputValue === setSelectedMunicipalitySlicer ||
      setInputValue === setSelectedDistrictSlicer ||
      setInputValue === setSelectedTerminalTypeSlicer ||
      setInputValue === setSelectedTerminalStatusSlicer ||
      setInputValue === setSelectedBudgetStatusSlicer ||
      setInputValue === setSelectedOrganizationInChargeSlicer
    ) {
      setInputValue([]);
    } else {
      setInputValue(null);
    }

    if (isTerminalSlicer) {
      resetSelectedItemIdAndTerminalFromList();
    }
  };

  const getDashboardFrameWidth = (isNumber = false) => {
    let baseWidth;

    if (isCollapsed) {
      baseWidth = 91.5;
    } else if (isComparisonMode) {
      baseWidth = 178.4;
    } else {
      baseWidth = 121.2;
    }

    return isNumber ? baseWidth : `${baseWidth}rem`;
  };

  const getMapWidth = () => {
    return isCollapsed ? "85.5rem" : "56.3rem";
  };

  // Exits comparison mode and resets all related state and data
  const handleCancelComparisonMode = async () => {
    setIsComparisonMode(false);
    setIsCollapsed(true);
    resetComparisonData();
    resetFilters();
    resetSelectedItemIdAndTerminalFromList();

    // Fetch original data again
    if (selectedModelVersion) {
      await fetchData(selectedModelVersion.modelVersionInt);
    } else if (modelVersions?.length > 0) {
      await fetchData(modelVersions[0].modelVersionInt);
    }
  };

  const isComparisonModeEnabled = () => {
    return !isComparisonMode && modelVersions && modelVersions.length > 1;
  };

  // Enters comparison mode by selecting appropriate second model version and fetching comparison data
  const handleSetComparisonMode = async () => {
    let versionToSelect = null;
    const selectedInt = Number(selectedModelVersion?.modelVersionInt);

    // Prefer pairing live (0) with the other selected/first frozen version
    if (selectedInt === 0) {
      versionToSelect = modelVersions.find((v) => Number(v.modelVersionInt) !== 0);
    } else if (!Number.isNaN(selectedInt)) {
      versionToSelect = modelVersions.find((v) => Number(v.modelVersionInt) === 0) ?? modelVersions.find((v) => Number(v.modelVersionInt) !== selectedInt);
    } else {
      versionToSelect = modelVersions.find((v) => v.modelVersionInt !== selectedModelVersion?.modelVersionInt);
    }

    if (versionToSelect) {
      setSelectedModelVersion2(versionToSelect);

      // Fetch comparison data with the selected versions
      await fetchComparisonData(selectedModelVersion.modelVersionInt, versionToSelect.modelVersionInt);
      setIsComparisonMode(true);
      setIsCollapsed(false);
    }
  };

  const markersWithFilterPolygons = useMemo(() => {
    const { markers } = mergeFilterPolygonsOntoMarkers(nationalModelForTerminalFiltered ?? [], filterData);
    return markers;
  }, [nationalModelForTerminalFiltered, filterData]);

  // For live version (0), polygons come from filter merge/cache; fetch remains fallback for other versions / gaps
  const mapMarkers = useMapMarkersPolygons(markersWithFilterPolygons, {
    modelVersionInt: selectedModelVersion?.modelVersionInt ?? 0,
    focusTerminalID: selectedTerminalFromList?.terminalID ?? null,
    fetchAllMissingPolygons: true,
  });

  return {
    dataIsReceived,
    userData,
    handleSearchTerminalIconClick,
    uniqueDistricts,
    handleSelectDropdownDistrict,
    selectedDistrict,
    uniqueMunicipalities,
    handleSelectDropdownMunicipality,
    selectedMunicipality,
    uniqueTerminals,
    handleSelectDropdownTerminal,
    selectedTerminal,
    showSearchTerminal,
    nationalModelForTerminalFiltered,
    mapMarkers,
    getZoomCoordinates,
    handleSelectTerminalFromList,
    activeTooltip,
    handleTooltipClick,
    getMainMeasurePercent,
    getMainMeasurePercentComparison,
    getSecondaryMeasurePercentForDisplay,
    getSecondaryMeasurePercentForDisplayComparison,
    getSecondaryMeasureDetails,
    tooltipRef,
    listRef,
    selectedItemId,
    getTerminalRowKey,
    isTerminalRowSelected,
    terminalNameSlicer,
    municipalitySlicer,
    districtSlicer,
    terminalTypeSlicer,
    terminalStatusSlicer,
    budgetStatusSlicer,
    organizationInChargeSlicer,
    selectedTerminalNameSlicer,
    handleSelectTerminalNameSlicer,
    selectedMunicipalitySlicer,
    handleSelectMunicipalitySlicer,
    selectedDistrictSlicer,
    handleSelectDistrictSlicer,
    selectedTerminalTypeSlicer,
    handleSelectTerminalTypeSlicer,
    selectedTerminalStatusSlicer,
    handleSelectTerminalStatusSlicer,
    selectedBudgetStatusSlicer,
    handleSelectBudgetStatusSlicer,
    selectedOrganizationInChargeSlicer,
    handleSelectOrganizationInChargeSlicer,
    exportToExcel,
    isCollapsed,
    handleToggleCollapse,
    getDashboardFrameWidth,
    getMapWidth,
    handleClear,
    hasActiveFilters,
    modelVersions,
    handleSelectModelVersion,
    selectedModelVersion,
    handleSelectModelVersion2,
    selectedModelVersion2,
    getFilteredModelVersionsForFirst,
    getFilteredModelVersionsForSecond,
    isModelVersionFrozen,
    isComparisonMode,
    handleCancelComparisonMode,
    handleSetComparisonMode,
    isComparisonModeEnabled,
  };
}
